'use client';

import Link from 'next/link';
import { stegaClean } from '@sanity/client/stega';

import { cn } from '@/lib/utils';
import { GroqImage } from '@/helpers/Image';
import { resolveI18nText } from '@/sanity/globals/I18n';
import { IBlogPostCard } from '@/sanity/lib/definitions';
import { formatBlogDate } from './formatBlogDate';
import { tidyTitle } from './tidyTitle';
import { useTrackedLink } from './useTrackedLink';

export default function BlogCard({
  post,
  language,
  className,
  titleAs: Title = 'h3',
  preload,
}: {
  post: IBlogPostCard;
  language: string;
  className?: string;
  titleAs?: 'h2' | 'h3';
  preload?: boolean;
}) {
  const lang = stegaClean(language);
  const href = useTrackedLink(stegaClean(post.slug), lang) ?? '#';
  const category = post.category
    ? resolveI18nText(post.category.name, post.category.nameI18n, lang)
    : '';
  const hotspot = post.mainImageHotspot;

  return (
    <Link
      href={href}
      className={cn(
        'group flex h-full flex-col rounded-[1.5rem] border border-black/10 bg-white [text-wrap:wrap] transition-shadow duration-300 hover:shadow-[0_1.75rem_5.5rem_rgba(0,0,0,0.08)] max-lg:gap-[3.375rem] max-lg:pb-[3.875rem] lg:gap-[4rem] lg:pb-[6rem]',
        className,
      )}
    >
      <div className="relative aspect-[449/273] w-full shrink-0 overflow-hidden rounded-t-[1.5rem]">
        {post.mainImage?.src ? (
          <GroqImage
            src={post.mainImage.src}
            alt={stegaClean(post.mainImageAlt) ?? ''}
            fill
            preload={preload}
            quality={100}
            sizes="(min-width: 993px) 30vw, (min-width: 640px) 50vw, 100vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            style={hotspot ? { objectPosition: `${hotspot.x * 100}% ${hotspot.y * 100}%` } : undefined}
          />
        ) : (
          <div className="h-full w-full bg-[#f0f0f0]" />
        )}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 from-[27%] to-transparent to-[60%]"
        />
        {category && (
          <span className="absolute left-[3rem] top-[3.125rem] rounded-full bg-[#0b6f53] px-[1.25rem] py-[0.625rem] font-geist text-[1.75rem] leading-[1.3] tracking-[-0.03em] text-white">
            {category}
          </span>
        )}
      </div>

      <div className="flex w-full flex-col gap-[2.5rem] text-black max-lg:px-[3.875rem] lg:px-[4.375rem]">
        <div className="flex flex-col gap-[1rem]">
          {post.publishAt && (
            <time
              dateTime={stegaClean(post.publishAt)}
              className="font-geist-mono text-[1.625rem] font-semibold uppercase leading-[1.33] text-black/[0.43]"
            >
              {formatBlogDate(post.publishAt, lang)}
            </time>
          )}
          {post.title && (
            <Title className="mb-0 line-clamp-3 font-geist text-[3rem] font-medium leading-[1.15] tracking-[-0.15rem] text-black">
              {tidyTitle(post.title)}
            </Title>
          )}
        </div>
        {post.excerpt && (
          <p className="mb-0 line-clamp-2 font-geist text-[2rem] font-normal leading-[1.5] text-black/70">
            {post.excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}
